import React from 'react';
import { alpha, Box, ButtonBase, Button, CircularProgress, Typography } from '@mui/material';
import { ChevronRight, Refresh } from '@mui/icons-material';
import type { ShopifyFieldOwner } from '../../../services/integrationApi';
import { tokens } from '../../../theme/tokens';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import { StatusChip } from '../../ui';
import { GroupLabel, SegmentedControl, StepCircle, v2CardSx } from './v2Ui';

const OWNER_OPTIONS: Array<{ value: ShopifyFieldOwner; label: string }> = [
  { value: 'motzu', label: 'Motzu owns' },
  { value: 'fill_blank', label: 'Fill blanks' },
  { value: 'never', label: 'Never touch' },
];

const OWNER_HINT: Record<ShopifyFieldOwner, string> = {
  motzu: 'Motzu writes it on every sync, the way your sync mode does today.',
  fill_blank: 'Motzu only fills it when the listing has none. Pick this if your team or ERP sets it.',
  never: 'Motzu leaves it alone. Everything below is kept but not written.',
};

export interface StepSummary {
  /** Fixed step id: 1 Store Taxonomy, 2 Name Rules, 3 Motzu Classification Overrides, 4 Unmatched Product Rules. */
  step: 1 | 2 | 3 | 4;
  label: string;
  count: string;
  hint: string;
  /** Shows an Optional chip: a step most stores leave empty. */
  optional?: boolean;
}

interface SummaryCardProps {
  /** "X of Y synced products categorised · none need a look"; null while it loads or when it is not available. */
  subline: string | null;
  sublineLoading?: boolean;
  sublineError?: string | null;
  discovering: boolean;
  /** Absent while the integration is still being created. */
  onDiscover?: () => void;
  ownersShown: boolean;
  categoryOwner: ShopifyFieldOwner;
  typeOwner: ShopifyFieldOwner;
  onCategoryOwner: (owner: ShopifyFieldOwner) => void;
  onTypeOwner: (owner: ShopifyFieldOwner) => void;
  /** Numbered 1..n in the order given, so a hidden taxonomy step leaves no gap. */
  steps: StepSummary[];
  /** Opens a step's own panel (its row in the list is a link to it). */
  onOpenStep: (step: StepSummary['step']) => void;
  /** Id of the panel title, which takes focus when the store comes back here from inside another panel. */
  headingId?: string;
}

const OwnerCard: React.FC<{
  label: string;
  value: ShopifyFieldOwner;
  onChange: (owner: ShopifyFieldOwner) => void;
}> = ({ label, value, onChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1.75, py: 1.5, border: '1px solid', borderColor: tokens.line.default, borderRadius: '11px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
      <Typography sx={{ flex: '1 1 auto', fontSize: 13, fontWeight: 700 }}>{label}</Typography>
      <SegmentedControl options={OWNER_OPTIONS} value={value} onChange={onChange} aria-label={`${label} owner`} />
    </Box>
    <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{OWNER_HINT[value]}</Typography>
  </Box>
);

/**
 * The Field ownership panel (v3): who writes each Shopify field, then the four decision steps in order,
 * each a row that opens that step's own panel.
 */
const SummaryCard: React.FC<SummaryCardProps> = ({
  subline,
  sublineLoading,
  sublineError,
  discovering,
  onDiscover,
  ownersShown,
  categoryOwner,
  typeOwner,
  onCategoryOwner,
  onTypeOwner,
  steps,
  onOpenStep,
  headingId,
}) => {
  const { t } = useAppTranslation();
  return (
    <Box sx={{ ...v2CardSx, px: 2.75, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <Typography component="h2" id={headingId} tabIndex={-1} sx={{ outline: 'none', fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', color: tokens.ink.primary }}>
            {ownersShown ? t('integrations.shopifyMapping.owners.title') : t('integrations.shopifyMapping.owners.titleNoOwners')}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: tokens.ink.muted, mt: 0.5 }}>
            {ownersShown ? t('integrations.shopifyMapping.owners.description') : t('integrations.shopifyMapping.owners.descriptionNoOwners')}
          </Typography>
          {sublineLoading && <CircularProgress size={14} sx={{ mt: 0.75 }} />}
          {subline && <Typography sx={{ fontSize: 13, color: tokens.ink.body, mt: 0.75 }}>{subline}</Typography>}
          {sublineError && <Typography sx={{ fontSize: 13, color: tokens.signal.caution.ink, mt: 0.75 }}>{sublineError}</Typography>}
        </Box>
        {onDiscover && (
          <Button
            variant="outlined"
            startIcon={discovering ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 16 }} />}
            disabled={discovering}
            onClick={onDiscover}
            sx={{ flex: '0 0 auto', height: 36, borderColor: tokens.line.default, borderRadius: '9px', color: tokens.ink.body, fontWeight: 600 }}
          >
            Re-discover store layout
          </Button>
        )}
      </Box>

      {ownersShown && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <GroupLabel>Who owns these fields</GroupLabel>
          <OwnerCard label="Shopify Product Category" value={categoryOwner} onChange={onCategoryOwner} />
          <OwnerCard label="Shopify Product Type" value={typeOwner} onChange={onTypeOwner} />
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <GroupLabel>How each product is decided · first match wins · set up only the steps you need</GroupLabel>
        {steps.map((item, index) => (
          <ButtonBase
            key={item.step}
            data-testid={`step-card-${item.step}`}
            onClick={() => onOpenStep(item.step)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1.25,
              px: 1.75,
              py: 1.375,
              textAlign: 'left',
              border: '1px solid',
              borderColor: tokens.line.default,
              borderRadius: '11px',
              bgcolor: item.step === 4 ? tokens.signal.caution.row : tokens.surface.card,
              '&:hover': { borderColor: alpha(tokens.accent.main, 0.5) },
            }}
          >
            <StepCircle n={index + 1} tone={item.step === 4 ? 'caution' : 'info'} />
            <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: tokens.ink.primary }}>
                {item.label}{' '}
                {item.count && <Box component="span" sx={{ fontWeight: 500, color: tokens.ink.faint }}>{item.count}</Box>}
                {item.optional && (
                  <Box component="span" sx={{ ml: 1, verticalAlign: 'middle' }}>
                    <StatusChip label="Optional" tone="neutral" />
                  </Box>
                )}
              </Typography>
              <Typography component="span" sx={{ fontSize: 12, color: tokens.ink.muted }}>{item.hint}</Typography>
            </Box>
            <ChevronRight aria-hidden sx={{ flex: '0 0 auto', fontSize: 18, color: tokens.ink.faint }} />
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
};

export default SummaryCard;
